import queryClient from '@/lib/queryClient'
import { RefundSettlementStatus } from '@/lib/enums/RefundSettlementStatus'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import { cashDrawerQueryKeys } from '@/lib/services/cash-drawers/cash-drawers.query'
import { reportQueryKeys } from '@/lib/services/reports/reports.query'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type OrganizationId,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { activityFeedQueryKeys } from '@/lib/services/activity-feed.query'
import { auditLogQueryKeys } from '@/lib/services/audit-log/audit-log.query'
import { organizationQueryKeys } from '@/lib/services/organizations/organizations.query'
import { customerQueryKeys } from '@/lib/services/customers/customers.query'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useSuspenseQuery,
  type QueryKey,
} from '@tanstack/react-query'
import type { RefundRequestStatusSnapshot } from '@/lib/services/refunds/refunds.types'
import {
  confirmCashRefundRequestPayment,
  createSalesOrderArrangementPhoto,
  deleteSalesOrderArrangementPhoto,
  deleteSalesOrderArrangementPhotoGallery,
  duplicateSalesOrderToCart,
  getRefundRequestStatus,
  getCancellationStatus,
  retryCancellationRefund,
  getInfiniteSalesOrders,
  getSalesOrder,
  getLocalPrintBundle,
  getSalesOrderAnalytics,
  getSalesOrderAssignees,
  getSalesOrderCalendarSummary,
  getSalesOrders,
  printSalesOrder,
  quoteSalesOrderRefund,
  refundExtraPayment,
  refundFullSalesOrder,
  refundPartialSalesOrder,
  replaceSalesOrderArrangementPhotoGallery,
  sendCustomerCopy,
  softDeleteSalesOrder,
  updateSalesOrderArrangementPhoto,
  updateSalesOrder,
  updateSalesOrderPurchaser,
  type SalesOrderOrganizationScope,
} from './sales-orders.api'
import type {
  SalesOrder,
  SalesOrderListItem,
  SalesOrderAnalyticsFilters,
  SalesOrderCancellationStatus,
  SalesOrderIdInput,
  SalesOrderIndexFilters,
  SalesOrderIndexResponse,
  SalesOrderPrintOptions,
} from './sales-orders.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface SalesOrderIndexScope extends OrganizationScope {
  filters?: SalesOrderIndexFilters
}

interface DeliveryRouteOrderScope extends OrganizationScope {
  /** `YYYY-MM-DD`. Without it, every open delivery order. */
  due?: string | null | undefined
}

interface SalesOrderInfiniteScope extends SalesOrderIndexScope {
  pageSize?: number
}

interface SalesOrderShowScope extends OrganizationScope {
  salesOrderId?: SalesOrderIdInput
}

type SalesOrderPrintScope = SalesOrderShowScope & SalesOrderPrintOptions

interface SalesOrderAnalyticsScope extends OrganizationScope {
  filters?: SalesOrderAnalyticsFilters
}

type RefundRequestIdInput = SalesOrderIdInput | null | undefined

/** A refund request id, or an object carrying one as `refundRequestId`. */
type RefundRequestIdSource = RefundRequestIdInput | { refundRequestId?: RefundRequestIdInput }

/** The status fetcher calls the endpoint itself rather than reading the key, so it needs the organization. */
interface RefundRequestStatusScope extends SalesOrderOrganizationScope {
  refundRequestId?: RefundRequestIdSource
}

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

/**
 * The detail query fetches a full `SalesOrder`, but shows the order's index row as placeholder data
 * until it arrives, so non-suspense readers can briefly see the slimmer list shape.
 */
export type SalesOrderShowData = SalesOrder | SalesOrderListItem

const getRefundRequestId = (input: RefundRequestIdSource): RefundRequestIdInput =>
  typeof input === 'object' && input !== null ? input.refundRequestId : input

function getSalesOrdersFromIndexResponse(response: SalesOrderIndexResponse | undefined) {
  return Array.isArray(response) ? response : (response?.data ?? [])
}

export const salesOrderQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'salesOrder' }, { organizationId }),
  ],
  index: ({ organizationId, filters }: SalesOrderIndexScope = {}) =>
    buildScopedAdminQueryKey(salesOrderQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
    }),
  assignees: ({ organizationId }: OrganizationScope = {}) =>
    buildScopedAdminQueryKey(salesOrderQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'assignees',
    }),
  infinite: ({ organizationId, filters, pageSize }: SalesOrderInfiniteScope = {}) =>
    buildScopedAdminQueryKey(salesOrderQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'infinite',
      filters,
      pageSize,
    }),
  show: ({ organizationId, salesOrderId }: SalesOrderShowScope = {}) =>
    buildScopedAdminQueryKey(salesOrderQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      salesOrderId: Number(salesOrderId),
    }),
  print: ({
    organizationId,
    paperHasPreprintedBranding,
    salesOrderId,
    template,
    layout,
  }: SalesOrderPrintScope = {}) =>
    buildScopedAdminQueryKey(salesOrderQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'print',
      salesOrderId: Number(salesOrderId),
      template,
      paperHasPreprintedBranding,
      ...(layout ? { layout } : {}),
    }),
  analytics: ({ organizationId, filters }: SalesOrderAnalyticsScope = {}) =>
    buildScopedAdminQueryKey(salesOrderQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'analytics',
      filters,
    }),
  calendarSummary: ({ organizationId, filters }: SalesOrderIndexScope = {}) =>
    buildScopedAdminQueryKey(salesOrderQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'calendarSummary',
      filters,
    }),
  refundRequestStatus: ({ organizationId, refundRequestId }: RefundRequestStatusScope) =>
    buildScopedAdminQueryKey(salesOrderQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'refundRequestStatus',
      refundRequestId: Number(getRefundRequestId(refundRequestId)),
    }),
}

export const salesOrderMutationKeys = {
  sendCustomerCopy: ['sales-order', 'sendCustomerCopy'],
  createArrangementPhoto: ['sales-order', 'createArrangementPhoto'],
  updateArrangementPhoto: ['sales-order', 'updateArrangementPhoto'],
  replaceArrangementPhotoGallery: ['sales-order', 'replaceArrangementPhotoGallery'],
  deleteArrangementPhoto: ['sales-order', 'deleteArrangementPhoto'],
  deleteArrangementPhotoGallery: ['sales-order', 'deleteArrangementPhotoGallery'],
  duplicateToCart: ['sales-order', 'duplicateToCart'],
}

/**
 * Follows replaced refund requests to the active one. Shared by the status query and the hook
 * that wraps it.
 */
async function fetchActiveRefundRequestStatus(
  organizationId: OrganizationId,
  resolvedRefundRequestId: RefundRequestIdInput
): Promise<RefundRequestStatusSnapshot> {
  let id: RefundRequestIdInput = resolvedRefundRequestId
  const visited = new Set<number>()
  while (!visited.has(Number(id))) {
    visited.add(Number(id))
    const status = await getRefundRequestStatus({ organizationId, refundRequestId: id })
    const activeId = status.active_refund_request_id ?? id
    if (!status.is_replaced || Number(activeId) === Number(id)) return status
    id = activeId
  }
  throw new Error('Unable to load the active refund. Refresh the order and try again.')
}

export const salesOrderQueries = {
  index: ({ organizationId, filters }: SalesOrderIndexScope = {}) =>
    indexTableQueryOptions({
      queryKey: salesOrderQueryKeys.index({ organizationId, filters }),
      queryFn: getSalesOrders,
    }),

  // Route create and edit ask the API for open delivery orders only; asking for every delivery
  // order a shop ever had grew without bound and ran past the API's memory limit.
  deliveryRouteOrders: ({ organizationId, due }: DeliveryRouteOrderScope = {}) => {
    const filters: SalesOrderIndexFilters = {
      filter: 'delivery',
      delivery_route_payload: 'items',
      delivery_route_candidates: true,
      ...(due ? { due } : {}),
    }

    return indexTableQueryOptions({
      queryKey: salesOrderQueryKeys.index({ organizationId, filters }),
      queryFn: getSalesOrders,
    })
  },

  assignees: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: salesOrderQueryKeys.assignees({ organizationId }),
      queryFn: getSalesOrderAssignees,
    }),

  infinite: ({ organizationId, filters, pageSize }: SalesOrderInfiniteScope = {}) =>
    infiniteQueryOptions({
      queryKey: salesOrderQueryKeys.infinite({ organizationId, filters, pageSize }),
      queryFn: getInfiniteSalesOrders,
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        const meta = Array.isArray(lastPage) ? undefined : lastPage.meta
        const currentPage = Number(meta?.current_page ?? 1)
        const lastPageNumber = Number(meta?.last_page ?? 1)

        return currentPage < lastPageNumber ? currentPage + 1 : undefined
      },
    }),

  show: ({ organizationId, salesOrderId }: SalesOrderShowScope = {}) =>
    queryOptions<
      SalesOrderShowData,
      Error,
      SalesOrderShowData,
      Parameters<typeof getSalesOrder>[0]['queryKey']
    >({
      queryKey: salesOrderQueryKeys.show({ organizationId, salesOrderId }),
      queryFn: getSalesOrder,
      // Use data from the sales order index route if available
      placeholderData: () =>
        getSalesOrdersFromIndexResponse(
          queryClient.getQueryData(salesOrderQueries.index({ organizationId }).queryKey)
        ).find((o) => o.id === Number(salesOrderId)),
    }),

  localPrintBundle: (scope: Parameters<typeof getLocalPrintBundle>[0]) =>
    queryOptions({
      queryKey: buildScopedAdminQueryKey(salesOrderQueryKeys.all(scope)[0], {
        ...scope,
        scope: 'local-print-bundle',
      }),
      queryFn: () => getLocalPrintBundle(scope),
      staleTime: 0,
    }),

  print: ({
    organizationId,
    paperHasPreprintedBranding,
    salesOrderId,
    template,
    layout,
  }: SalesOrderPrintScope = {}) =>
    queryOptions({
      queryKey: salesOrderQueryKeys.print({
        organizationId,
        paperHasPreprintedBranding,
        layout,
        salesOrderId,
        template,
      }),
      queryFn: printSalesOrder,
      staleTime: 0, // always get the latest info
    }),

  analytics: ({ organizationId, filters }: SalesOrderAnalyticsScope = {}) =>
    queryOptions({
      queryKey: salesOrderQueryKeys.analytics({ organizationId, filters }),
      queryFn: getSalesOrderAnalytics,
    }),

  calendarSummary: ({ organizationId, filters }: SalesOrderIndexScope = {}) =>
    queryOptions({
      queryKey: salesOrderQueryKeys.calendarSummary({ organizationId, filters }),
      queryFn: getSalesOrderCalendarSummary,
    }),

  refundRequestStatus: ({ organizationId, refundRequestId }: RefundRequestStatusScope) => {
    const resolvedRefundRequestId = getRefundRequestId(refundRequestId)

    return queryOptions({
      queryKey: salesOrderQueryKeys.refundRequestStatus({
        organizationId,
        refundRequestId: resolvedRefundRequestId,
      }),
      queryFn: () => fetchActiveRefundRequestStatus(organizationId, resolvedRefundRequestId),
    })
  },
}

export function useSalesOrders({
  organizationId,
  filters,
  ...params
}: SalesOrderIndexScope & QueryParams<ReturnType<typeof salesOrderQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...salesOrderQueries.index({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useSalesOrderAssignees({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof salesOrderQueries.assignees>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...salesOrderQueries.assignees({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useInfiniteSalesOrders({
  organizationId,
  filters,
  pageSize,
  ...params
}: SalesOrderInfiniteScope & QueryParams<ReturnType<typeof salesOrderQueries.infinite>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useInfiniteQuery({
    ...salesOrderQueries.infinite({
      organizationId: resolvedOrganizationId,
      filters,
      pageSize,
    }),
    ...params,
  })
}

export function useSalesOrder(
  salesOrderId: SalesOrderIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof salesOrderQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)
  return useSuspenseQuery({
    ...salesOrderQueries.show({ organizationId: resolvedOrganizationId, salesOrderId }),
    ...params,
  })
}

export function useSalesOrderAnalytics(
  filters: SalesOrderAnalyticsFilters | undefined,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof salesOrderQueries.analytics>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...salesOrderQueries.analytics({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useSalesOrderCalendarSummary({
  organizationId,
  filters,
  ...params
}: SalesOrderIndexScope & QueryParams<ReturnType<typeof salesOrderQueries.calendarSummary>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...salesOrderQueries.calendarSummary({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useUpdateSalesOrderMutation({
  organizationId: scopedOrganizationId,
}: OrganizationScope = {}) {
  const organizationId = useRouteOrganizationId(scopedOrganizationId)
  return useMutation({
    mutationFn: (values: Omit<Parameters<typeof updateSalesOrder>[0], 'organizationId'>) =>
      updateSalesOrder({ organizationId, ...values }),
    onSuccess: (_, values) =>
      invalidateOrderUpdateQueries(organizationId, Boolean(values?.payment_terms_code)),
  })
}

export function useUpdateSalesOrderPurchaserMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: Omit<Parameters<typeof updateSalesOrderPurchaser>[0], 'organizationId'>) =>
      updateSalesOrderPurchaser({ organizationId, ...values }),
    onSuccess: () => invalidateOrderUpdateQueries(organizationId, true),
  })
}

/** What reads again once an order changes; a change to what the customer is billed reads their balances too. */
export function invalidateOrderUpdateQueries(
  organizationId: number,
  changesCustomerBilling: boolean
) {
  const keys: QueryKey[] = [
    salesOrderQueryKeys.all({ organizationId }),
    activityFeedQueryKeys.all({ organizationId }),
    auditLogQueryKeys.all({ organizationId }),
    organizationQueryKeys.show(organizationId),
  ]
  if (changesCustomerBilling) keys.push(customerQueryKeys.all({ organizationId }))
  return Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
}

export function useRefundPartialSalesOrderMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: Omit<Parameters<typeof refundPartialSalesOrder>[0], 'organizationId'>) =>
      refundPartialSalesOrder({ organizationId, ...values }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: salesOrderQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: cashDrawerQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: reportQueryKeys.cashDrawer({ organizationId }) }),
      ]),
  })
}

export function useRefundFullSalesOrderMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: Omit<Parameters<typeof refundFullSalesOrder>[0], 'organizationId'>) =>
      refundFullSalesOrder({ organizationId, ...values }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: salesOrderQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: cashDrawerQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: reportQueryKeys.cashDrawer({ organizationId }) }),
      ]),
  })
}

/** Returns payment the order holds above its total, as an order change that lowered it leaves. */
export function useRefundExtraPaymentMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: Omit<Parameters<typeof refundExtraPayment>[0], 'organizationId'>) =>
      refundExtraPayment({ organizationId, ...values }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: salesOrderQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: cashDrawerQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: reportQueryKeys.cashDrawer({ organizationId }) }),
      ]),
    // A refusal can mean the extra payment changed since the order was read: read it again, so a retry names the
    // amount the order holds now.
    onError: () =>
      queryClient.invalidateQueries({ queryKey: salesOrderQueryKeys.all({ organizationId }) }),
  })
}

export function useRefundQuoteMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: Omit<Parameters<typeof quoteSalesOrderRefund>[0], 'organizationId'>) =>
      quoteSalesOrderRefund({ organizationId, ...values }),
  })
}

export function fetchRefundRequestStatus({
  organizationId,
  refundRequestId,
}: RefundRequestStatusScope) {
  return queryClient.fetchQuery(
    salesOrderQueries.refundRequestStatus({ organizationId, refundRequestId })
  )
}

export function useRefundRequestStatus({
  refundRequestId,
  salesOrderId,
  initialStatus,
}: {
  refundRequestId?: RefundRequestIdSource
  salesOrderId?: SalesOrderIdInput
  initialStatus?: RefundRequestStatusSnapshot | null
}) {
  const orgId = useRouteOrganizationId()
  const options = salesOrderQueries.refundRequestStatus({
    organizationId: orgId,
    refundRequestId,
  })
  return useQuery({
    ...options,
    queryFn: async () => {
      const status = await fetchActiveRefundRequestStatus(
        orgId,
        getRefundRequestId(refundRequestId)
      )
      // A pending request can still advance a leg or unlock a cash handoff.
      await queryClient.invalidateQueries({
        queryKey: salesOrderQueryKeys.show({ organizationId: orgId, salesOrderId }),
      })
      return status
    },
    initialData: initialStatus ?? undefined,
    initialDataUpdatedAt: 0,
    enabled: (query) =>
      Boolean(refundRequestId) &&
      (!query.state.data ||
        query.state.data.is_replaced ||
        query.state.data.settlement_status === RefundSettlementStatus.PendingSettlement.Value),
    staleTime: 5000,
    retry: false,
    refetchInterval: false,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: false,
  })
}

export function useConfirmCashRefundRequestPaymentMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (
      values: Omit<Parameters<typeof confirmCashRefundRequestPayment>[0], 'organizationId'>
    ) => confirmCashRefundRequestPayment({ organizationId, ...values }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: salesOrderQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: cashDrawerQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: reportQueryKeys.cashDrawer({ organizationId }) }),
      ]),
  })
}

export function useCancellationStatus({
  salesOrderId,
  initialStatus,
}: {
  salesOrderId: SalesOrderIdInput
  initialStatus?: SalesOrderCancellationStatus | Record<string, never>
}) {
  const organizationId = useRouteOrganizationId()
  return useQuery({
    queryKey: [...salesOrderQueryKeys.all({ organizationId }), 'cancellation', salesOrderId],
    queryFn: () => getCancellationStatus({ organizationId, salesOrderId }),
    initialData: initialStatus,
    refetchInterval: (query) => (query.state.data?.status === 'processing' ? 5000 : false),
  })
}

export function useRetryCancellationRefundMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: Omit<Parameters<typeof retryCancellationRefund>[0], 'organizationId'>) =>
      retryCancellationRefund({ organizationId, ...values }),
    onSuccess: () =>
      Promise.all([
        invalidateOrderUpdateQueries(organizationId, true),
        queryClient.invalidateQueries({ queryKey: cashDrawerQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: reportQueryKeys.cashDrawer({ organizationId }) }),
      ]),
  })
}

export function useSoftDeleteSalesOrderMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    /** Accepts the full input, or just the order id. */
    mutationFn: (
      values: Omit<Parameters<typeof softDeleteSalesOrder>[0], 'organizationId'> | SalesOrderIdInput
    ) => {
      const payload = values && typeof values === 'object' ? values : { salesOrderId: values }

      return softDeleteSalesOrder({ organizationId, ...payload })
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: salesOrderQueryKeys.all({ organizationId }),
        }),
        queryClient.invalidateQueries({
          queryKey: activityFeedQueryKeys.all({ organizationId }),
        }),
        queryClient.invalidateQueries({
          queryKey: organizationQueryKeys.show(organizationId),
        }),
        queryClient.invalidateQueries({
          queryKey: cashDrawerQueryKeys.all({ organizationId }),
        }),
        queryClient.invalidateQueries({
          queryKey: reportQueryKeys.cashDrawer({ organizationId }),
        }),
      ])
    },
  })
}

export function useSendCustomerCopyMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: salesOrderMutationKeys.sendCustomerCopy,
    mutationFn: ({
      salesOrderId,
      email,
    }: {
      salesOrderId: SalesOrderIdInput
      email?: string | null
    }) => sendCustomerCopy({ organizationId, salesOrderId, email }),
    onSuccess: (_, { salesOrderId }) =>
      queryClient.invalidateQueries({
        queryKey: salesOrderQueryKeys.show({ organizationId, salesOrderId }),
      }),
  })
}

export function useCreateSalesOrderArrangementPhotoMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: salesOrderMutationKeys.createArrangementPhoto,
    mutationFn: (
      values: Omit<
        NonNullable<Parameters<typeof createSalesOrderArrangementPhoto>[0]>,
        'organizationId'
      >
    ) => createSalesOrderArrangementPhoto({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: salesOrderQueryKeys.all({ organizationId }),
      }),
  })
}

export function useUpdateSalesOrderArrangementPhotoMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: salesOrderMutationKeys.updateArrangementPhoto,
    mutationFn: (
      values: Omit<
        NonNullable<Parameters<typeof updateSalesOrderArrangementPhoto>[0]>,
        'organizationId'
      >
    ) => updateSalesOrderArrangementPhoto({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: salesOrderQueryKeys.all({ organizationId }),
      }),
  })
}

export function useReplaceSalesOrderArrangementPhotoGalleryMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: salesOrderMutationKeys.replaceArrangementPhotoGallery,
    mutationFn: (
      values: Omit<
        NonNullable<Parameters<typeof replaceSalesOrderArrangementPhotoGallery>[0]>,
        'organizationId'
      >
    ) => replaceSalesOrderArrangementPhotoGallery({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: salesOrderQueryKeys.all({ organizationId }),
      }),
  })
}

export function useDeleteSalesOrderArrangementPhotoMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: salesOrderMutationKeys.deleteArrangementPhoto,
    mutationFn: (
      values: Omit<
        NonNullable<Parameters<typeof deleteSalesOrderArrangementPhoto>[0]>,
        'organizationId'
      >
    ) => deleteSalesOrderArrangementPhoto({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: salesOrderQueryKeys.all({ organizationId }),
      }),
  })
}

export function useDeleteSalesOrderArrangementPhotoGalleryMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: salesOrderMutationKeys.deleteArrangementPhotoGallery,
    mutationFn: (
      values: Omit<
        NonNullable<Parameters<typeof deleteSalesOrderArrangementPhotoGallery>[0]>,
        'organizationId'
      >
    ) => deleteSalesOrderArrangementPhotoGallery({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: salesOrderQueryKeys.all({ organizationId }),
      }),
  })
}

export function useDuplicateSalesOrderToCartMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: salesOrderMutationKeys.duplicateToCart,
    mutationFn: (salesOrderId: SalesOrderIdInput) =>
      duplicateSalesOrderToCart({ organizationId, salesOrderId }),
  })
}
